import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Navbar from "@/components/Navbar";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "sonner";

const ESTADOS_MX = [
  "Aguascalientes", "Baja California", "Baja California Sur", "Campeche", "CDMX",
  "Chiapas", "Chihuahua", "Coahuila", "Colima", "Durango", "Estado de México",
  "Guanajuato", "Guerrero", "Hidalgo", "Jalisco", "Michoacán", "Morelos", "Nayarit",
  "Nuevo León", "Oaxaca", "Puebla", "Querétaro", "Quintana Roo", "San Luis Potosí",
  "Sinaloa", "Sonora", "Tabasco", "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatán", "Zacatecas",
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [plazas, setPlazas] = useState([]);
  const [modoTienda, setModoTienda] = useState("independiente"); // "independiente" | "plaza"
  const [form, setForm] = useState({
    nombre: "", email: "", password: "", telefono: "",
    role: "cliente", plaza_id: "", nombre_local: "", numero_local: "", categoria: "", vehiculo: "",
    direccion: "", colonia: "", ciudad: "", estado: "", codigo_postal: "",
    whatsapp_gerente: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/plazas").then(r => setPlazas(r.data.plazas)); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (form.role === "cliente" || form.role === "neni") {
        ["plaza_id","nombre_local","numero_local","categoria","vehiculo",
         "direccion","colonia","ciudad","estado","codigo_postal","whatsapp_gerente"].forEach(k => delete payload[k]);
      }
      if (form.role === "tienda") {
        if (modoTienda === "independiente") { payload.plaza_id = null; payload.numero_local = ""; }
        if (!payload.ciudad && !payload.estado && !payload.codigo_postal && !payload.plaza_id) {
          toast.error("Indica al menos ciudad, estado o CP");
          setLoading(false); return;
        }
      }
      if (form.role === "repartidor" && !payload.plaza_id) {
        payload.plaza_id = null; // repartidor independiente / nacional
      }
      const u = await register(payload);
      toast.success(`Bienvenido, ${u.nombre}`);
      const map = { plaza_admin: "/admin", tienda: "/tienda", repartidor: "/repartidor/onboarding", cliente: "/cliente", neni: "/neni" };
      nav(map[u.role] || "/");
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <Navbar />
      <div className="max-w-lg mx-auto px-6 py-16">
        <div className="overline text-zinc-500">Registro</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Crear cuenta</h1>
        <p className="text-sm text-zinc-600 mt-2">Únete a <b>en 90 MINUTOS</b> · disponible en todo México</p>

        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div>
            <Label>Soy…</Label>
            <Select value={form.role} onValueChange={v => set("role", v)}>
              <SelectTrigger data-testid="reg-role" className="rounded-sm h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cliente">Cliente (comprador)</SelectItem>
                <SelectItem value="tienda">Locatario / Tienda</SelectItem>
                <SelectItem value="repartidor">Repartidor</SelectItem>
                <SelectItem value="neni">NENI (vendedora independiente)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nombre</Label>
              <Input data-testid="reg-nombre" value={form.nombre} onChange={e => set("nombre", e.target.value)} required className="mt-1 rounded-sm h-11" />
            </div>
            <div>
              <Label>Celular (10 dígitos)</Label>
              <Input data-testid="reg-telefono" type="tel" inputMode="tel" placeholder="55 1234 5678" value={form.telefono} onChange={e => set("telefono", e.target.value)} required className="mt-1 rounded-sm h-11" />
            </div>
          </div>
          <div>
            <Label>Contraseña</Label>
            <PasswordInput testId="reg-password" value={form.password} onChange={e => set("password", e.target.value)} required minLength={6} className="mt-1 rounded-sm h-11" />
            <p className="text-[11px] text-zinc-500 mt-1">Entrarás con tu celular y esta contraseña.</p>
          </div>
          <details className="text-sm">
            <summary className="cursor-pointer text-zinc-500 hover:text-zinc-900">Agregar email (opcional)</summary>
            <Input data-testid="reg-email" type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="tucorreo@ejemplo.com" className="mt-2 rounded-sm h-11" />
          </details>

          {form.role === "tienda" && (
            <>
              <div className="bg-white border border-zinc-200 rounded-sm p-4">
                <Label className="mb-2 block">¿Cómo opera tu negocio?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button" data-testid="modo-independiente"
                    onClick={() => setModoTienda("independiente")}
                    className={`p-3 rounded-sm border-2 text-left transition-colors ${modoTienda === "independiente" ? "border-[#FF3366] bg-[#FF3366]/5" : "border-zinc-200"}`}
                  >
                    <div className="font-display font-bold text-sm">Independiente</div>
                    <div className="text-xs text-zinc-500 mt-0.5">Local propio · barrio · comercio</div>
                  </button>
                  <button
                    type="button" data-testid="modo-plaza"
                    onClick={() => setModoTienda("plaza")}
                    className={`p-3 rounded-sm border-2 text-left transition-colors ${modoTienda === "plaza" ? "border-[#FF3366] bg-[#FF3366]/5" : "border-zinc-200"}`}
                  >
                    <div className="font-display font-bold text-sm">En una plaza</div>
                    <div className="text-xs text-zinc-500 mt-0.5">Centro comercial · mall</div>
                  </button>
                </div>
              </div>
              {modoTienda === "plaza" && (
                <div data-testid="plaza-box" className="bg-white border border-zinc-200 rounded-sm p-4">
                  <Label>Plaza o centro comercial</Label>
                  <Select value={form.plaza_id} onValueChange={v => set("plaza_id", v)}>
                    <SelectTrigger data-testid="reg-plaza" className="rounded-sm h-11"><SelectValue placeholder="Selecciona plaza" /></SelectTrigger>
                    <SelectContent>
                      {plazas.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="mt-3">
                    <Label>Número de local</Label>
                    <Input data-testid="reg-numero-local" value={form.numero_local} onChange={e => set("numero_local", e.target.value)} placeholder="Ej. L-12" className="mt-1 rounded-sm h-11" />
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-2">Los locales dentro de una plaza comparten el envío único del mall.</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Nombre del local</Label>
                  <Input data-testid="reg-nombre-local" value={form.nombre_local} onChange={e => set("nombre_local", e.target.value)} required className="mt-1 rounded-sm h-11" />
                </div>
                <div>
                  <Label>Categoría</Label>
                  <Input data-testid="reg-categoria" value={form.categoria} onChange={e => set("categoria", e.target.value)} placeholder="Moda, Tech, Comida…" className="mt-1 rounded-sm h-11" required />
                </div>
                <div>
                  <Label>WhatsApp del gerente</Label>
                  <Input data-testid="reg-whatsapp" value={form.whatsapp_gerente} onChange={e => set("whatsapp_gerente", e.target.value)} placeholder="+52 55…" className="mt-1 rounded-sm h-11" required />
                </div>
              </div>
              {/* Ubicación Global */}
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 space-y-3">
                <div className="overline text-zinc-500">Ubicación de tu negocio</div>
                <div>
                  <Label>Dirección</Label>
                  <Input data-testid="reg-direccion" value={form.direccion} onChange={e => set("direccion", e.target.value)} placeholder="Calle, número" className="mt-1 rounded-sm h-11" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Colonia</Label>
                    <Input data-testid="reg-colonia" value={form.colonia} onChange={e => set("colonia", e.target.value)} className="mt-1 rounded-sm h-11" />
                  </div>
                  <div>
                    <Label>Código postal</Label>
                    <Input data-testid="reg-cp" value={form.codigo_postal} onChange={e => set("codigo_postal", e.target.value)} className="mt-1 rounded-sm h-11" maxLength={5} />
                  </div>
                  <div>
                    <Label>Ciudad</Label>
                    <Input data-testid="reg-ciudad" value={form.ciudad} onChange={e => set("ciudad", e.target.value)} required={modoTienda === "independiente"} className="mt-1 rounded-sm h-11" />
                  </div>
                  <div>
                    <Label>Estado</Label>
                    <Select value={form.estado} onValueChange={v => set("estado", v)}>
                      <SelectTrigger data-testid="reg-estado" className="mt-1 rounded-sm h-11"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                      <SelectContent>
                        {ESTADOS_MX.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </>
          )}

          {form.role === "repartidor" && (
            <>
              <div>
                <Label>Plaza (opcional — deja vacío si cubres varias zonas)</Label>
                <Select value={form.plaza_id} onValueChange={v => set("plaza_id", v)}>
                  <SelectTrigger data-testid="reg-plaza" className="rounded-sm h-11"><SelectValue placeholder="Sin plaza (nacional)" /></SelectTrigger>
                  <SelectContent>
                    {plazas.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vehículo</Label>
                <Input data-testid="reg-vehiculo" value={form.vehiculo} onChange={e => set("vehiculo", e.target.value)} placeholder="Moto, Bicicleta, etc." className="mt-1 rounded-sm h-11" />
              </div>
            </>
          )}

          <Button type="submit" data-testid="reg-submit" disabled={loading} className="w-full h-11 rounded-sm bg-[#FF3366] hover:bg-[#E62958]">
            {loading ? "Creando…" : "Crear cuenta"}
          </Button>
          <div className="mt-3 text-center text-xs text-zinc-500">Tu tienda se publica al activar el PLAN INTEGRAL ($299 MXN / 30 días) desde tu panel.</div>
        </form>
        <div className="mt-6 text-center text-sm text-zinc-600">
          ¿Ya tienes cuenta? <Link to="/login" className="text-[#FF3366] font-medium hover:underline">Entrar</Link>
        </div>
      </div>
    </div>
  );
}
